import {
    NativeRequestPayload,
    NativeRequestType,
    NativeResponsePayload,
    NativeResponseType,
    NativeRequest,
    LogClevertapEventRequestPayload,
} from "@swiggy-private/native-bridge";
import { BridgeHandler, STATUS_CODES } from "@swiggy-private/web-bridge-sdk";
import { safeJsonParse } from "@swiggy-private/js-utils";
import { handleMwebUpiIntentRedirect } from "@checkout-core/helpers/PaymentHelpers";
import {
    isIxigoUpiBridgeAvailable,
    ixigoExpandToAllFormats,
    ixigoResolveToPackageName,
} from "@checkout-core/helpers/Common";
import { handleLoginFlowNavigation } from "@checkout-core/helpers/LoginFlowNavigation";
import { mwebUpiIntentAvailableMethods, MWEB_UPI_REDIRECT_STATUS } from "@checkout-core/constants/MwebUpiAppWhitelist";
import { GlobalHeaderState } from "./Headers";
import { SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import MoengageWebSdk from "@moengage/web-sdk";
import { SOURCE_CHANNEL } from "../constants";

export const getPreCartLinkFromStorage = () => {
    if (typeof window !== "undefined" && typeof window.sessionStorage !== "undefined") {
        try {
            const value = String(sessionStorage.getItem("swgy_mweb_static_web_food_cart_session_prev_link") || "");
            return value;
        } catch (_) {
            return "";
        }
    }
    return "";
};

export const customHandlers: Partial<
    Record<NativeRequestType, BridgeHandler<NativeRequestPayload, NativeResponsePayload>>
> = {
    [NativeRequestType.CLOSE_WEBVIEW]: {
        handle: async () => {
            /*
             * TODO: Add DO BL check
             * This component requires a custom back handling for the special usecase through which it is rendered.
             * We are using redirection logic to load the DO cart on web, and the usual browser provided convention
             * of using histoy.back() does not work here. This is because of the custom MiniRouter than CW uses,
             * it pushes a state manually once it is loaded, so during redirection from caller web-client e.g, SEO,
             * browser pushes state first to the history stack and CW also pushes a state in history stack,
             * which causes problem with back handling. To revert its effect, we are doing history.go(-2) to undo
             * 2 states instead of just 1 through history.back()
             */

            // Redirect to menu page if coming from login flow (for all flows)
            const navigationResponse = await handleLoginFlowNavigation();
            if (navigationResponse) {
                return navigationResponse;
            }

            // Default behavior: go back 2 steps in history
            history.go(-2);

            return { statusCode: 0, type: NativeResponseType.SUCCESS, requestId: "" };
        },
    },

    /**
     * Ixigo UPI Intent: Get available UPI apps
     * When running in Ixigo webview, fetch UPI apps from native bridge
     * Otherwise, fall back to default mweb UPI apps list
     */
    [NativeRequestType.IS_UPI_INTENT_METHOD_AVAILABLE]: {
        handle: async (): Promise<{ payload?: NativeResponsePayload; statusCode?: number }> => {
            // If Ixigo bridge is available, use it exclusively
            if (isIxigoUpiBridgeAvailable() && window.ixigoBridge?.getUPIAppsList) {
                try {
                    const ixigoAppsRaw = await window.ixigoBridge.getUPIAppsList();
                    const ixigoApps: Array<{ package: string }> =
                        typeof ixigoAppsRaw === "string" ? (safeJsonParse(ixigoAppsRaw, []) ?? []) : ixigoAppsRaw;
                    const methods = ixigoApps.flatMap((app) => ixigoExpandToAllFormats(app.package));
                    return {
                        payload: { methods } as NativeResponsePayload,
                        statusCode: STATUS_CODES.SUCCESS_CODE,
                    };
                } catch {
                    // Ixigo bridge failed - return empty methods (no fallback to mweb)
                    return {
                        payload: { methods: [] } as NativeResponsePayload,
                        statusCode: STATUS_CODES.ERROR,
                    };
                }
            }

            // Default (non-Ixigo): return whitelisted mweb UPI apps
            return {
                payload: mwebUpiIntentAvailableMethods as NativeResponsePayload,
                statusCode: STATUS_CODES.SUCCESS_CODE,
            };
        },
    },

    /**
     * Ixigo UPI Intent: Open UPI app for payment
     * When running in Ixigo webview, open UPI app via native bridge
     * Otherwise, fall back to default mweb redirect behavior
     */
    [NativeRequestType.PAY_USING_UPI_INTENT]: {
        handle: async (
            req: NativeRequest<NativeRequestPayload>,
        ): Promise<{ payload?: NativeResponsePayload; statusCode?: number }> => {
            const payload = req.payload as {
                upiParams: { orderId: string; url: string; methodName: string };
            };
            const { orderId, url, methodName } = payload.upiParams;

            // If Ixigo bridge is available, use it for UPI app opening
            if (isIxigoUpiBridgeAvailable() && window.ixigoBridge?.openUPIApp) {
                try {
                    const packageName = ixigoResolveToPackageName(methodName);
                    await window.ixigoBridge.openUPIApp(packageName, url);
                    return {
                        payload: { status: MWEB_UPI_REDIRECT_STATUS.SUCCESS, orderId } as NativeResponsePayload,
                        statusCode: STATUS_CODES.SUCCESS_CODE,
                    };
                } catch {
                    return {
                        payload: { status: MWEB_UPI_REDIRECT_STATUS.FAILURE } as NativeResponsePayload,
                        statusCode: STATUS_CODES.ERROR,
                    };
                }
            }

            // Default (non-Ixigo): use mweb redirect implementation
            try {
                const result = await handleMwebUpiIntentRedirect(url, orderId);
                return { payload: result as NativeResponsePayload, statusCode: STATUS_CODES.SUCCESS_CODE };
            } catch {
                return {
                    payload: { status: MWEB_UPI_REDIRECT_STATUS.FAILURE } as NativeResponsePayload,
                    statusCode: STATUS_CODES.ERROR,
                };
            }
        },
    },
    [NativeRequestType.LOG_CLEVERTAP_EVENT]: {
        handle: async (
            req: NativeRequest<NativeRequestPayload>,
        ): Promise<{ payload?: NativeResponsePayload; statusCode?: number }> => {
            const channel = GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) || "";
            if (channel !== SOURCE_CHANNEL.JIO_HOTSTAR) {
                return {
                    payload: {},
                    statusCode: STATUS_CODES.SUCCESS_CODE,
                };
            }
            try {
                const payload = req.payload as LogClevertapEventRequestPayload;
                if (payload.eventName) {
                    const triggerEvent = async () => {
                        return MoengageWebSdk.trackEvent(payload.eventName, {
                            ...payload.params,
                            channel,
                        });
                    };
                    if (!MoengageWebSdk.isSdkEnabled()) {
                        await MoengageWebSdk.enableSdk();
                        await triggerEvent();
                    } else {
                        await triggerEvent();
                    }
                }
                return {
                    payload: {},
                    statusCode: STATUS_CODES.SUCCESS_CODE,
                };
            } catch (error) {
                return {
                    payload: {},
                    statusCode: STATUS_CODES.ERROR,
                };
            }
        },
    },
};
